import { Grid } from "@mui/material";
import Paper from "@mui/material/Paper";
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from 'prop-types';
import EnhancedConfirmButton from "../enhanced/EnhancedConfirmButton";
import EnhancedTableToolbar from "../enhanced/EnhancedTableToolbar";
import { exportCompanyReceptionistsCash, exportSummarizationValues } from "../../features/advancedReportSlice";
import EnhancedDateField from "../enhanced/EnhancedDateField";
import EnhancedSelect from "../enhanced/EnhancedSelect";
import { fetchCompanyList } from "../../features/listsSlice";
import alertHelper from "../../helpers/alertHelper";
import { getDBDateFromMomentObject } from "../../helpers/DateConvertor";

// Constants
const SCENARIOS = {
  CARD_SLEVOMAT: "card-slevomat",
  RECEPTIONISTS_COMPANY: "receptionists-company"
};

const SCENARIO_LIST = [
  { id: SCENARIOS.CARD_SLEVOMAT, name: "Report tržeb kartou a slevomatů" },
  { id: SCENARIOS.RECEPTIONISTS_COMPANY, name: "Výkaz hotovostních tržeb za pobočku a recepční" },
];

const ERROR_MESSAGES = {
  INVALID_DATE_SINCE: "Datum od je v nevalidním stavu",
  INVALID_DATE_TO: "Datum do je v nevalidním stavu",
  MISSING_COMPANY: "Pro tento scénář je nutné vybrat organizaci",
  UNKNOWN_SCENARIO: "Neznámý scénář reportu"
};

// Custom hooks
const useFormState = () => {
  const [formState, setFormState] = useState({});
  
  const updateFormState = useCallback((field, value) => {
    setFormState(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const resetForm = useCallback(() => {
    setFormState({});
  }, []);

  return { formState, updateFormState, resetForm };
};

const useValidation = () => {
  const validateDates = useCallback((dateSince, dateTo) => {
    if (typeof dateSince !== "object") {
      alertHelper.error({ message: ERROR_MESSAGES.INVALID_DATE_SINCE, duration: 3000 });
      return false;
    }

    if (typeof dateTo !== "object") {
      alertHelper.error({ message: ERROR_MESSAGES.INVALID_DATE_TO, duration: 3000 });
      return false;
    }

    return true;
  }, []);

  const validateCompanySelection = useCallback((scenario, companyId) => {
    if (scenario === SCENARIOS.RECEPTIONISTS_COMPANY && !companyId) {
      alertHelper.error({ message: ERROR_MESSAGES.MISSING_COMPANY, duration: 3000 });
      return false;
    }
    return true;
  }, []);

  const validateScenario = useCallback((scenario) => {
    if (!scenario) {
      alertHelper.error({ message: ERROR_MESSAGES.UNKNOWN_SCENARIO, duration: 3000 });
      return false;
    }
    
    const validScenarios = Object.values(SCENARIOS);
    if (!validScenarios.includes(scenario)) {
      alertHelper.error({ message: ERROR_MESSAGES.UNKNOWN_SCENARIO, duration: 3000 });
      return false;
    }
    
    return true;
  }, []);

  return { validateDates, validateCompanySelection, validateScenario };
};

// Form field components
const DateRangeFields = ({ formState, onDateChange, classes }) => (
  <>
    <Grid item xs={6} className={classes.formControl}>
      <EnhancedDateField
        name="dateSince"
        value={formState.dateSince}
        onChange={(event) => onDateChange('dateSince', event)}
        label="Datum od"
      />
    </Grid>
    <Grid item xs={6} className={classes.formControl}>
      <EnhancedDateField
        name="dateTo"
        value={formState.dateTo}
        onChange={(event) => onDateChange('dateTo', event)}
        label="Datum do"
      />
    </Grid>
  </>
);

const ScenarioField = ({ formState, onFieldChange, classes }) => (
  <Grid item xs={12} className={classes.formControl}>
    <EnhancedSelect
      name="scenario"
      value={formState.scenario}
      label="Scénář"
      list={SCENARIO_LIST}
      onChange={onFieldChange}
    />
  </Grid>
);

const CompanyField = ({ formState, allowedCompanyList, onFieldChange, classes }) => {
  const shouldShowCompanyField = formState.scenario === SCENARIOS.RECEPTIONISTS_COMPANY && allowedCompanyList.length > 0;
  
  if (!shouldShowCompanyField) return null;

  return (
    <Grid item xs={12} className={classes.formControl}>
      <EnhancedSelect
        name="companyId"
        value={formState.companyId}
        label="Organizace"
        list={allowedCompanyList}
        itemId="id"
        itemName="name"
        onChange={onFieldChange}
      />
    </Grid>
  );
};

// Main component
function AdvancedReport() {
  const dispatch = useDispatch();
  const classes = useStyles();
  const allowedCompanyList = useSelector((state) => state.lists.companyList);
  
  const { formState, updateFormState } = useFormState();
  const { validateDates, validateCompanySelection, validateScenario } = useValidation();

  useEffect(() => {
    dispatch(fetchCompanyList());
  }, [dispatch]);

  const handleFieldChange = useCallback((event) => {
    event.persist();
    updateFormState(event.target.name, event.target.value);
  }, [updateFormState]);

  const handleDateChange = useCallback((field, value) => {
    updateFormState(field, value);
  }, [updateFormState]);

  const handleGenerate = useCallback(() => {
    const { dateSince, dateTo, scenario, companyId } = formState;

    // Validate dates
    if (!validateDates(dateSince, dateTo)) {
      return;
    }

    // Validate scenario
    if (!validateScenario(scenario)) {
      return;
    }

    // Validate company selection for specific scenario
    if (!validateCompanySelection(scenario, companyId)) {
      return;
    }

    const apiData = {
      dateSince: getDBDateFromMomentObject(dateSince),
      dateTo: getDBDateFromMomentObject(dateTo)
    };

    // Dispatch appropriate action based on scenario
    switch (scenario) {
      case SCENARIOS.CARD_SLEVOMAT:
        dispatch(exportSummarizationValues(apiData));
        break;
      case SCENARIOS.RECEPTIONISTS_COMPANY:
        dispatch(exportCompanyReceptionistsCash({ ...apiData, companyId }));
        break;
      default:
        // This should never happen due to validation, but keeping as fallback
        console.warn('Unknown scenario:', scenario);
    }
  }, [formState, validateDates, validateScenario, validateCompanySelection, dispatch]);

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <EnhancedTableToolbar label="Sumarizační reporty" />
        <Grid container spacing={1}>
          <DateRangeFields 
            formState={formState} 
            onDateChange={handleDateChange} 
            classes={classes} 
          />
          <ScenarioField 
            formState={formState} 
            onFieldChange={handleFieldChange} 
            classes={classes} 
          />
          <CompanyField 
            formState={formState} 
            allowedCompanyList={allowedCompanyList} 
            onFieldChange={handleFieldChange} 
            classes={classes} 
          />
          <Grid item xs={12}>
            <EnhancedConfirmButton 
              handleOk={handleGenerate} 
              okLabel="Vygenerovat report" 
            />
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
}

// PropTypes
DateRangeFields.propTypes = {
  formState: PropTypes.object.isRequired,
  onDateChange: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

ScenarioField.propTypes = {
  formState: PropTypes.object.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

CompanyField.propTypes = {
  formState: PropTypes.object.isRequired,
  allowedCompanyList: PropTypes.array.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

// Styles
const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    paddingTop: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(2),
  },
  formControl: {
    // Add any specific form control styles here
  }
}));

export default AdvancedReport;
